import { useTranslation } from "react-i18next";
import type { FacebookPageCandidate } from "@multi-chat/shared";
import { ChannelMark } from "../../../components/channel/ChannelMark.js";
import { AppNotice } from "../../../components/common/AppNotice.js";
import { Button } from "../../../components/ui/button/index.js";
import { MixedLocaleValue, TechnicalValue } from "../TechnicalValue.js";
import { cn } from "../../../lib/utils.js";

interface FacebookPageSelectionProps {
  candidates: FacebookPageCandidate[];
  selectedPageId: string | null;
  canSelectPage: boolean;
  canCancelPending: boolean;
  busy: boolean;
  onSelectPage: (id: string) => void;
  onSubmitSelection: () => void;
  onCancelPending: () => void;
}

export function FacebookPageSelection(props: FacebookPageSelectionProps): React.JSX.Element {
  const { t } = useTranslation();
  const {
    candidates,
    selectedPageId,
    canSelectPage,
    canCancelPending,
    busy,
    onSelectPage,
    onSubmitSelection,
    onCancelPending,
  } = props;

  return (
    <section
      className="mx-auto grid w-full max-w-lg gap-4 rounded-xl border border-line bg-surface p-5 shadow-sm"
      data-testid="facebook-page-selection"
      aria-labelledby="facebook-page-selection-title"
    >
      <div className="flex items-center gap-3">
        <ChannelMark platformType="FACEBOOK" size="md" />
        <div className="grid min-w-0 gap-0.5 text-start">
          <h3 id="facebook-page-selection-title" className="m-0 text-[1rem] font-semibold text-ink">
            {t("facebook.pending.dialogTitle")}
          </h3>
          <p className="m-0 text-[0.75rem] text-muted">{t("facebook.pending.dialogHint")}</p>
        </div>
      </div>

      <AppNotice
        severity="success"
        description={t("facebook.pending.authorized")}
        testId="facebook-page-selection-authorized"
      />

      <div className="grid gap-1 text-start">
        <p className="m-0 text-[0.875rem] font-semibold text-ink">{t("facebook.pending.title")}</p>
        <p className="m-0 text-[0.8125rem] leading-relaxed text-muted">{t("facebook.pending.message")}</p>
      </div>

      {candidates.length === 0 ? (
        <p className="m-0 rounded-lg border border-dashed border-line px-4 py-6 text-center text-[0.8125rem] text-muted">
          {t("facebook.pending.empty")}
        </p>
      ) : (
        <ul
          className="m-0 grid list-none gap-2 p-0"
          role="radiogroup"
          aria-label={t("facebook.pending.title")}
        >
          {candidates.map((page) => {
            const isSelected = selectedPageId === page.pageId;
            return (
              <li key={page.pageId}>
                <label
                  className={cn(
                    "flex cursor-pointer items-center gap-3 rounded-lg border border-line bg-surface px-3 py-2.5 text-start transition-colors hover:bg-surface-hover",
                    isSelected && "border-accent bg-surface-selected",
                  )}
                >
                  <input
                    type="radio"
                    name="facebook-page-selection"
                    className="size-4 shrink-0 cursor-pointer accent-accent"
                    checked={isSelected}
                    onChange={() => onSelectPage(page.pageId)}
                    disabled={busy || !canSelectPage}
                  />
                  <ChannelMark platformType="FACEBOOK" size="sm" />
                  <span className="grid min-w-0 gap-0.5">
                    <MixedLocaleValue className="text-[0.875rem] font-semibold wrap-anywhere">
                      {page.pageName}
                    </MixedLocaleValue>
                    <TechnicalValue className="text-[0.75rem] text-muted tabular-nums wrap-anywhere">
                      {page.pageId}
                    </TechnicalValue>
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      )}

      {canSelectPage || canCancelPending ? (
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line pt-3">
          {canCancelPending ? (
            <Button type="button" variant="ghost" onClick={onCancelPending} disabled={busy}>
              {t("facebook.pending.cancel")}
            </Button>
          ) : null}
          {canSelectPage ? (
            <Button type="button" onClick={onSubmitSelection} disabled={busy || !selectedPageId}>
              {busy ? t("facebook.pending.saving") : t("facebook.pending.confirm")}
            </Button>
          ) : null}
        </div>
      ) : (
        <p className="m-0 text-[0.8125rem] text-muted">{t("facebook.readonly")}</p>
      )}
    </section>
  );
}
